// Shuriken PMO-P — Global Data & Theme Context
import React, { createContext, useContext, useState, useCallback } from "react";
import { THEMES, getTheme } from "./theme";

const DataContext = createContext(null);

const emptySlot = () => ({
  data: [], columns: [], fileName: "", result: null, lastRefresh: null,
});

export function DataProvider({ children }) {
  // Theme state — persisted in localStorage
  const [themeName, setThemeNameState] = useState(
    () => localStorage.getItem("pmo_theme") || THEMES.DARK_NAVY
  );
  const theme = getTheme(themeName);

  const setTheme = useCallback((name) => {
    localStorage.setItem("pmo_theme", name);
    setThemeNameState(name);
  }, []);

  // Per-page dataset slots
  const [datasets, setDatasets] = useState({
    portfolio:      emptySlot(),
    health:         emptySlot(),
    benefits:       emptySlot(),
    risk:           emptySlot(),
    resources:      emptySlot(),
    financial:      emptySlot(),
    schedule:       emptySlot(),
    dependencies:   emptySlot(),
    workforce:      emptySlot(),
    rationalization:emptySlot(),
    executive:      emptySlot(),
    aiagent:        emptySlot(),
  });

  // MS Project Server connection state
  const [msProjectConnection, setMsProjectConnection] = useState({
    connected: false,
    url: "",
    mode: "demo", // "demo" | "odata" | "sql"
    lastSync: null,
    projectCount: 0,
  });

  const setDataset = useCallback((page, payload) => {
    setDatasets(prev => ({
      ...prev,
      [page]: { ...prev[page], ...payload, lastRefresh: new Date().toISOString() },
    }));
  }, []);

  const clearDataset = useCallback((page) => {
    setDatasets(prev => ({ ...prev, [page]: emptySlot() }));
  }, []);

  const clearAll = useCallback(() => {
    const cleared = {};
    Object.keys(datasets).forEach(k => { cleared[k] = emptySlot(); });
    setDatasets(cleared);
  }, [datasets]);

  return (
    <DataContext.Provider value={{
      theme, themeName, setTheme,
      datasets, setDataset, clearDataset, clearAll,
      msProjectConnection, setMsProjectConnection,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useTheme must be inside DataProvider");
  return { theme: ctx.theme, themeName: ctx.themeName, setTheme: ctx.setTheme };
}

export function useDataset(page) {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useDataset must be inside DataProvider");
  return {
    dataset:      ctx.datasets[page] || emptySlot(),
    setDataset:   (payload) => ctx.setDataset(page, payload),
    clearDataset: () => ctx.clearDataset(page),
    clearAll:     ctx.clearAll,
  };
}

export function useMSProject() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useMSProject must be inside DataProvider");
  return {
    connection: ctx.msProjectConnection,
    setConnection: ctx.setMsProjectConnection,
  };
}
